import React, { useState, useEffect, useCallback } from 'react';

const CURRENCIES = ['USD', 'SGD', 'RMB', 'TWD'];
const MAX_FILE_BYTES = 5 * 1024 * 1024;

export default function TopUpModal({ isOpen, onClose, onSuccess }) {
  const [method, setMethod] = useState('stripe');
  const [amountCp, setAmountCp] = useState('');
  const [currency, setCurrency] = useState('SGD');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');

  const [rates, setRates] = useState(null);
  const [settings, setSettings] = useState(null);
  const [topups, setTopups] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchRates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/exchange-rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setRates(await res.json());
    } catch (err) {
      console.error('Failed to fetch exchange rates:', err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-topup-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data.settings);
      }
    } catch (err) {
      console.error('Failed to fetch top-up settings:', err);
    }
  }, []);

  const fetchTopups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-topup-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTopups(data.topups || []);
      }
    } catch (err) {
      console.error('Failed to fetch top-up history:', err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccessMessage('');
      setAmountCp('');
      setFile(null);
      setFileError('');
      fetchRates();
      fetchSettings();
      fetchTopups();
    }
  }, [isOpen, fetchRates, fetchSettings, fetchTopups]);

  if (!isOpen) return null;

  const parsedAmount = parseFloat(amountCp);
  const validAmount = !isNaN(parsedAmount) && parsedAmount > 0;

  const spreadPercent = rates?.member_buy_spread_percent ?? 0;
  const usdtFeePercent = rates?.usdt_fee_percent ?? 0;

  const computeQuote = () => {
    if (!validAmount || !rates) return null;
    if (method === 'usdt') {
      const usdRate = rates.USD?.rate;
      if (!usdRate) return null;
      const spreadAdjusted = usdRate * (1 + spreadPercent / 100);
      const usdt = (parsedAmount * spreadAdjusted) * (1 + usdtFeePercent / 100);
      return { amount: usdt, currency: 'USDT' };
    }
    if (method === 'paynow') {
      const sgdRate = rates.SGD?.rate;
      if (!sgdRate) return null;
      const spreadAdjusted = sgdRate * (1 + spreadPercent / 100);
      return { amount: parsedAmount * spreadAdjusted, currency: 'SGD' };
    }
    // stripe and bank_transfer both use the member "Buying" rate for the chosen currency.
    // rate is currency-units-per-1-Credit-Point, so CP -> currency is a multiply.
    const rate = rates[currency]?.rate;
    if (!rate) return null;
    const spreadAdjusted = rate * (1 + spreadPercent / 100);
    return { amount: parsedAmount * spreadAdjusted, currency };
  };

  const quote = computeQuote();

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFileError('');
    if (!f) {
      setFile(null);
      return;
    }
    if (!f.type.startsWith('image/')) {
      setFileError('Please upload an image file.');
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError('Image must be 5MB or smaller.');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const handleSubmitStripe = async () => {
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-topup-create-stripe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount_cp: parsedAmount, currency }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        setError(data.message || data.error || 'Failed to start card payment.');
        setSubmitting(false);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      console.error('Top-up Stripe error:', err);
      setError('Failed to start card payment.');
      setSubmitting(false);
    }
  };

  const handleSubmitManual = async () => {
    if (!file) {
      setFileError('A proof-of-payment image is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('method', method);
      form.append('amount_cp', parsedAmount);
      if (method === 'bank_transfer') form.append('currency', currency);
      form.append('proof_image', file);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-topup-create-manual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || data.error || 'Failed to submit top-up request.');
        setSubmitting(false);
        return;
      }
      setSuccessMessage('Top-up request submitted. Awaiting admin approval.');
      setAmountCp('');
      setFile(null);
      await fetchTopups();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Top-up manual submit error:', err);
      setError('Failed to submit top-up request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTopup = async (topupId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-topup-cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topup_id: topupId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to cancel top-up');
      if (data.credited) {
        setSuccessMessage(data.message || 'This top-up was already paid, so it has been credited.');
        if (onSuccess) onSuccess();
      }
      await fetchTopups();
    } catch (err) {
      console.error('Cancel top-up error:', err);
      setError(err.message || 'Failed to cancel top-up request.');
    }
  };

  const handleSubmit = () => {
    if (!validAmount) {
      setError('Please enter a valid top-up amount.');
      return;
    }
    if (method === 'stripe') {
      handleSubmitStripe();
    } else {
      handleSubmitManual();
    }
  };

  const statusLabel = { awaiting_payment: 'Awaiting payment', pending_review: 'Awaiting admin approval', approved: 'Approved', rejected: 'Rejected' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Top Up Credit Points</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          {error && <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          {successMessage && <div className="p-4 bg-green-50 border border-green-200 rounded text-sm text-green-700">{successMessage}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Method</label>
            <div className="grid grid-cols-2 gap-2">
              {[['stripe', 'Card (Stripe)'], ['usdt', 'USDT'], ['bank_transfer', 'Bank Transfer'], ['paynow', 'PayNow']].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => { setMethod(val); setError(''); setFileError(''); }}
                  className={`px-3 py-2 rounded-md border text-sm font-medium transition ${method === val ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount (Credit Points)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amountCp}
              onChange={(e) => setAmountCp(e.target.value)}
              placeholder="e.g. 100.00"
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {(method === 'stripe' || method === 'bank_transfer') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {quote && (
            <div className="p-3 bg-blue-50 rounded">
              <p className="text-sm text-gray-600">You will {method === 'stripe' ? 'pay' : 'send'}</p>
              <p className="text-lg font-bold text-blue-600">
                {quote.amount.toFixed(2)} {quote.currency}
              </p>
              {method === 'usdt' && <p className="text-xs text-gray-500 mt-1">Includes {usdtFeePercent.toFixed(2)}% USDT processing fee</p>}
            </div>
          )}

          {method === 'usdt' && settings && (
            <div className="p-3 bg-gray-50 rounded space-y-2">
              {settings.usdt_wallet_address && (
                <>
                  <p className="text-sm text-gray-600">Send USDT to</p>
                  <p className="font-mono text-sm break-all text-gray-900">{settings.usdt_wallet_address}</p>
                </>
              )}
              {settings.usdt_qr_url && (
                <img src={settings.usdt_qr_url} alt="USDT QR code" className="w-40 h-40 object-contain mx-auto bg-white rounded border" />
              )}
            </div>
          )}

          {method === 'bank_transfer' && settings && (
            <div className="p-3 bg-gray-50 rounded space-y-1 text-sm text-gray-900">
              <p><span className="text-gray-600">Bank:</span> {settings.bank_name || '—'}</p>
              <p><span className="text-gray-600">Account Number:</span> {settings.bank_account_number || '—'}</p>
              <p><span className="text-gray-600">Account Holder:</span> {settings.bank_account_holder || '—'}</p>
              {settings.bank_swift_code && <p><span className="text-gray-600">SWIFT:</span> {settings.bank_swift_code}</p>}
            </div>
          )}

          {method === 'paynow' && settings && (
            <div className="p-3 bg-gray-50 rounded space-y-2 text-center">
              <p className="text-sm text-gray-600">Scan to pay via PayNow</p>
              {settings.paynow_qr_url ? (
                <img src={settings.paynow_qr_url} alt="PayNow QR code" className="w-40 h-40 object-contain mx-auto bg-white rounded border" />
              ) : (
                <p className="text-sm text-gray-500">PayNow QR code not yet configured.</p>
              )}
            </div>
          )}

          {(method === 'usdt' || method === 'bank_transfer' || method === 'paynow') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Proof of Payment (image, max 5MB)</label>
              <input type="file" accept="image/*" onChange={handleFileChange} disabled={submitting} />
              {fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
              {file && !fileError && <p className="text-xs text-green-600 mt-1">{file.name} ready to upload</p>}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !validAmount}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition disabled:opacity-50"
            >
              {submitting ? 'Processing...' : method === 'stripe' ? 'Pay with Card' : 'Submit for Approval'}
            </button>
          </div>

          {topups.length > 0 && (
            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">My Top-Ups</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {topups.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                    <span className="text-gray-700">{t.amount_cp} CP via {t.method}</span>
                    <div className="flex items-center gap-2">
                      <span className={
                        t.status === 'approved' ? 'text-green-600'
                        : t.status === 'rejected' ? 'text-red-600'
                        : 'text-yellow-600'
                      }>
                        {statusLabel[t.status] || t.status}
                      </span>
                      {t.status === 'awaiting_payment' && (
                        <button
                          type="button"
                          onClick={() => handleCancelTopup(t.id)}
                          className="text-xs text-gray-400 hover:text-red-600 underline"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
